import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { CubeLoader } from '@/components/ui/cube-loader'
import { GENERATE_AUDIENCE_HYPOTHESES } from '@/graphql/audience-hypothesis-queries'
import { GENERATE_ADD_AUDIENCE_HYPOTHESES } from '@/graphql/add-audience-queries'
import AudienceTypeChip from '@/components/mediaBuying/adCampaign/AudienceTypeChip'
import TargetingOptionList from '@/components/mediaBuying/adCampaign/TargetingOptionList'
import BudgetMovePanel from './BudgetMovePanel'
import { budgetMoveAmountProblem, getBudgetMoveErrorMessage, withDonorInput } from '@/utils/budget-move'
import {
    MAX_REQUESTED_PERSONAS,
    applyGeneratedSet,
    buildGenerationVariables,
    buildSetInputs,
    describeBudgetGate,
    getBuilderState,
    getHypothesisMap,
    isSetStale,
    isSuggestedAudienceKind,
    leaveAiMode,
    mapAudienceBuilderError,
    parseBudgetAmount,
    setBuilderField,
    setExistingAudiencesMode,
    startAiMode,
    SUGGESTIONS_NOTE,
    toggleHypothesis,
} from '@/utils/audience-builder'

const formatReach = (hypothesis) => {
    const { reachLower, reachUpper } = hypothesis
    if (reachLower == null || reachUpper == null) return null
    return `estimated reach ${Number(reachLower).toLocaleString()} - ${Number(reachUpper).toLocaleString()} people`
}

const HypothesisRow = ({ hypothesis, checked, onToggle }) => {
    const reach = formatReach(hypothesis)
    const interestCount = hypothesis.interests.length
    const suggested = isSuggestedAudienceKind(hypothesis.kind)
    const interestNoun = interestCount === 1 ? 'interest' : 'interests'
    const details = [
        interestCount > 0 ? `${interestCount} ${suggested ? 'suggested ' : ''}${interestNoun}` : null,
        reach,
    ].filter(Boolean)

    return (
        <li className="tw:flex tw:gap-3 tw:px-5 tw:py-4" data-testid={`ai-audience-${hypothesis.id}`}>
            <Checkbox
                checked={checked}
                onCheckedChange={() => onToggle(hypothesis.id)}
                aria-label={`Use ${hypothesis.label}`}
                className="tw:mt-1"
            />
            <div className="tw:flex tw:flex-col tw:gap-1.5">
                <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                    <span className="tw:text-sm tw:font-semibold">{hypothesis.label}</span>
                    <AudienceTypeChip kind={hypothesis.kind} />
                </div>
                {hypothesis.rationale && <p className="tw:text-sm tw:text-gray-200">{hypothesis.rationale}</p>}
                {details.length > 0 && <p className="tw:text-xs tw:text-gray-200">{details.join(', ')}</p>}
                {interestCount > 0 && (
                    <ul
                        className="tw:flex tw:flex-wrap tw:gap-1.5"
                        aria-label={`${suggested ? 'Suggested interests' : 'Interests'} for ${hypothesis.label}`}
                    >
                        {hypothesis.interests.map((interest) => (
                            <li key={interest.id} className="tw:rounded tw:bg-gray-500 tw:px-2 tw:py-0.5 tw:text-xs">
                                {interest.name}
                            </li>
                        ))}
                    </ul>
                )}
                <TargetingOptionList
                    options={hypothesis.targetingOptions}
                    label={`${suggested ? 'Suggested behaviours and demographics' : 'Behaviours and demographics'} for ${hypothesis.label}`}
                />
                {suggested && (
                    <p className="tw:text-xs tw:text-gray-200" data-testid="suggestions-note">
                        {SUGGESTIONS_NOTE}
                    </p>
                )}
                {hypothesis.unavailableTermCount > 0 && (
                    <p className="tw:text-xs tw:text-gray-200">
                        {hypothesis.unavailableTermCount}{' '}
                        {hypothesis.unavailableTermCount === 1
                            ? 'suggestion is no longer offered'
                            : 'suggestions are no longer offered'}
                    </p>
                )}
            </div>
        </li>
    )
}

/**
 * AI audience option for the acquisition stage of Sales Funnel Setup.
 *
 * `data` is the Sales Funnel Setup step data and `onChange(nextData)` stores a new version of it.
 * `inputs` carries what generation depends on: campaignId, publisherId, objective and location.
 * `objectiveSupported` is false for objectives without AI audiences: only leaving AI mode is offered then.
 * `budgetMove` is the eligibility of an existing ad campaign when AI audiences are added to it: their budget is
 * then an amount moved from one of its ad sets instead of a new acquisition budget.
 *
 * A generation result is applied to the step data as it is when the result arrives, so changes made in
 * the meantime are kept. A result is ignored when a newer request was started, AI mode was left or the
 * panel is no longer shown.
 */
const AiAudiencePanel = ({ data, onChange, inputs, currency, objectiveSupported = true, budgetMove = null }) => {
    const builder = getBuilderState(data)
    const hypothesisMap = getHypothesisMap(data)
    const selectedIds = data?.audienceSelections?.acquisition || []
    const [generate, { loading }] = useMutation(
        budgetMove ? GENERATE_ADD_AUDIENCE_HYPOTHESES : GENERATE_AUDIENCE_HYPOTHESES,
    )
    const [budgetInput, setBudgetInput] = useState(builder.acquisitionBudget ?? '')
    const [errorMessage, setErrorMessage] = useState(null)
    const latest = useRef({ data, onChange })
    latest.current = { data, onChange }
    const requestCount = useRef(0)
    const mounted = useRef(false)

    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])

    useEffect(() => {
        setBudgetInput(builder.acquisitionBudget ?? '')
    }, [builder.acquisitionBudget])

    const budgetAmount = parseBudgetAmount(budgetInput)
    const set = builder.set
    const hypotheses = Object.values(hypothesisMap)
    const gate = describeBudgetGate(set)
    const currentInputs = withDonorInput(
        buildSetInputs({
            ...inputs,
            acquisitionBudget: budgetAmount || 0,
            requestedPersonas: builder.requestedPersonas,
        }),
        data,
    )
    const stale = isSetStale(set, currentInputs)
    const recommendedCount = set?.plan?.recommended_persona_count
    const recommendedLabel = Number.isInteger(recommendedCount)
        ? `Recommended (${recommendedCount} for this budget)`
        : 'Recommended for the budget'
    const donorAdSetId = builder.budgetMove?.donorAdSetId || null
    const moveProblem = budgetMove
        ? budgetMoveAmountProblem(
              budgetInput,
              budgetMove.donors.find((donor) => donor.ad_set_id === donorAdSetId && donor.usable),
              budgetMove.min_daily_budget,
              budgetMove.currency,
          )
        : null
    const canGenerate = !!budgetAmount && !moveProblem

    const runGeneration = async (regenerate) => {
        if (!canGenerate || loading) return
        setErrorMessage(null)
        const request = ++requestCount.current
        const requestBudget = String(budgetInput)
        const requestInputs = withDonorInput(
            buildSetInputs({
                ...inputs,
                acquisitionBudget: budgetAmount,
                requestedPersonas: builder.requestedPersonas,
            }),
            data,
        )
        const variables = buildGenerationVariables({
            ...inputs,
            acquisitionBudget: budgetAmount,
            requestedPersonas: builder.requestedPersonas,
            regenerate,
        })
        if (budgetMove) {
            variables.adCampaignId = inputs.adCampaignId
            variables.donorAdSetId = donorAdSetId
        }
        const isCurrent = () =>
            mounted.current && request === requestCount.current && getBuilderState(latest.current.data).mode === 'ai'
        try {
            const response = await generate({ variables })
            const result = response?.data?.generateAudienceHypotheses
            if (!result) throw new Error('empty result')
            if (!isCurrent()) return
            const withBudget = setBuilderField(latest.current.data, 'acquisitionBudget', requestBudget)
            latest.current.onChange(applyGeneratedSet(withBudget, result, requestInputs))
        } catch (error) {
            if (isCurrent()) setErrorMessage(getBudgetMoveErrorMessage(error) || mapAudienceBuilderError(error))
        }
    }

    if (!objectiveSupported) {
        return (
            <div
                className="tw:mx-6 tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:p-4"
                data-testid="ai-audience-card"
            >
                <span className="tw:text-sm tw:font-semibold">Turba AI Audience</span>
                <p className="tw:text-sm tw:text-gray-200" role="status">
                    AI audiences are not available for this campaign objective. Use the existing audiences instead.
                </p>
                <Button variant="secondary" size="sm" className="tw:w-fit" onClick={() => onChange(leaveAiMode(data))}>
                    Use existing audiences instead
                </Button>
            </div>
        )
    }

    if (builder.mode !== 'ai') {
        return (
            <div
                className="tw:mx-6 tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:p-4"
                data-testid="ai-audience-card"
            >
                <div className="tw:flex tw:flex-col tw:gap-1">
                    <span className="tw:text-sm tw:font-semibold">Turba AI Audience</span>
                    <span className="tw:text-sm tw:text-gray-200">
                        Advantage+ broad delivery plus up to 3 AI-built audience suggestions, checked against Meta's
                        current targeting options.
                    </span>
                    {budgetMove && (
                        <span className="tw:text-sm tw:text-gray-200">
                            For this campaign the new audiences get their budget from one of its ad sets, so the total
                            daily budget stays the same.
                        </span>
                    )}
                </div>
                <Button variant="secondary" size="sm" className="tw:w-fit" onClick={() => onChange(startAiMode(data))}>
                    Use AI Audience
                </Button>
            </div>
        )
    }

    return (
        <div
            className="tw:mx-6 tw:flex tw:flex-col tw:gap-4 tw:rounded-lg tw:border tw:p-4"
            data-testid="ai-audience-card"
        >
            <div className="tw:flex tw:flex-col tw:gap-1">
                <span className="tw:text-sm tw:font-semibold">Turba AI Audience</span>
                <span className="tw:text-sm tw:text-gray-200">
                    AI audiences are added to your campaign. Existing audiences you select run alongside them.
                </span>
            </div>

            <div className="tw:flex tw:items-start tw:gap-2">
                <Switch
                    checked={builder.advanced}
                    onCheckedChange={(checked) => onChange(setExistingAudiencesMode(data, checked))}
                    id="ai-audience-existing"
                    aria-labelledby="ai-audience-existing-label"
                    aria-describedby="ai-audience-existing-help"
                    disabled={loading}
                    className="tw:mt-0.5"
                />
                <div className="tw:flex tw:flex-col tw:gap-0.5">
                    <span id="ai-audience-existing-label" className="tw:text-sm">
                        Also run my existing audiences (Broad, Interest, Facebook Fans...)
                    </span>
                    <span id="ai-audience-existing-help" className="tw:text-xs tw:text-gray-200">
                        Run them next to the AI audiences to compare results. Budget is shared across all selected
                        audiences.
                    </span>
                </div>
            </div>

            {budgetMove ? (
                <BudgetMovePanel
                    eligibility={budgetMove}
                    data={data}
                    onChange={onChange}
                    amount={budgetInput}
                    onAmountChange={setBudgetInput}
                    onAmountBlur={() => onChange(setBuilderField(data, 'acquisitionBudget', String(budgetInput)))}
                    currency={currency}
                    disabled={loading}
                />
            ) : (
                <div className="tw:flex tw:flex-col tw:gap-1.5 tw:max-w-xs">
                    <label htmlFor="ai-audience-budget" className="tw:text-sm">
                        Daily acquisition budget{currency ? ` (${currency})` : ''}
                    </label>
                    <Input
                        id="ai-audience-budget"
                        type="number"
                        min="0"
                        step="1"
                        value={budgetInput}
                        onChange={(event) => setBudgetInput(event.target.value)}
                        onBlur={() => onChange(setBuilderField(data, 'acquisitionBudget', String(budgetInput)))}
                        disabled={loading}
                    />
                </div>
            )}

            <div className="tw:flex tw:flex-col tw:gap-1.5 tw:max-w-xs">
                <label htmlFor="ai-audience-personas" className="tw:text-sm">
                    Number of persona audiences
                </label>
                <select
                    id="ai-audience-personas"
                    className="tw:h-9 tw:rounded-xs tw:border tw:border-[#3B5168] tw:bg-transparent tw:px-3 tw:text-sm"
                    value={builder.requestedPersonas ?? ''}
                    onChange={(event) =>
                        onChange(
                            setBuilderField(
                                data,
                                'requestedPersonas',
                                event.target.value === '' ? null : Number(event.target.value),
                            ),
                        )
                    }
                    disabled={loading}
                >
                    <option value="">{recommendedLabel}</option>
                    {Array.from({ length: MAX_REQUESTED_PERSONAS + 1 }, (_, count) => (
                        <option key={count} value={count}>
                            {count === 0 ? '0 (broad only)' : count}
                        </option>
                    ))}
                </select>
            </div>

            {loading && (
                <div className="tw:flex tw:items-center tw:gap-3" role="status">
                    <CubeLoader size="small" />
                    <span className="tw:text-sm">Building audiences. This can take up to 30 seconds.</span>
                </div>
            )}

            {errorMessage && (
                <p className="tw:text-sm tw:text-red-300" role="alert">
                    {errorMessage}
                </p>
            )}

            {set && !loading && (
                <div className="tw:flex tw:flex-col tw:gap-3">
                    <div className="tw:flex tw:flex-col tw:gap-1" data-testid="ai-audience-gate">
                        {gate.summary && <p className="tw:text-sm tw:font-medium">{gate.summary}</p>}
                        {gate.details.map((text) => (
                            <p key={text} className="tw:text-sm tw:text-gray-200">
                                {text}
                            </p>
                        ))}
                        {gate.warnings.map((text) => (
                            <p key={text} className="tw:text-sm tw:text-yellow-300" role="status">
                                {text}
                            </p>
                        ))}
                    </div>
                    {stale && (
                        <p className="tw:text-sm tw:text-yellow-300" role="status">
                            The campaign details or budget changed since these audiences were built. Regenerate them
                            before continuing.
                        </p>
                    )}
                    <ul className="tw:flex tw:flex-col tw:divide-y tw:rounded tw:border">
                        {hypotheses.map((hypothesis) => (
                            <HypothesisRow
                                key={hypothesis.id}
                                hypothesis={hypothesis}
                                checked={selectedIds.includes(hypothesis.id)}
                                onToggle={(id) => onChange(toggleHypothesis(data, id))}
                            />
                        ))}
                    </ul>
                </div>
            )}

            <div className="tw:flex tw:flex-wrap tw:gap-2">
                {set ? (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => runGeneration(true)}
                        disabled={loading || !canGenerate}
                    >
                        Regenerate
                    </Button>
                ) : (
                    <Button size="sm" onClick={() => runGeneration(false)} disabled={loading || !canGenerate}>
                        Build AI audiences
                    </Button>
                )}
                <Button variant="secondary" size="sm" onClick={() => onChange(leaveAiMode(data))} disabled={loading}>
                    Remove AI audiences
                </Button>
            </div>
        </div>
    )
}

export default AiAudiencePanel
